// Withholding Tax Detail — Tax Reports → "WHT Statement" tab.
// One row per invoice on which tax was withheld, in the client's layout:
//   Trans. Date | Trans. No. | Ref. Trans. No. | Ref. Date | Bill No. | Party |
//   NTN No. | Tax | Taxable Amount | Rate | Withholding Amount
// Three sections, each with its own total:
//   payment — WHT we deducted from vendors (Cash / Bank Payments per invoice paid,
//             advance WHT, WHT taken on the purchase invoice itself, purchase
//             returns reverse it)
//   receipt — WHT customers deducted from us (Cash / Bank Receipts per invoice,
//             WHT taken on the sale / non-tax sale invoice itself)
//   advance — advance tax collected on Sale Invoices ("Add to total", 236H)
// Taxable amount = the amount the rate was applied to (as saved); where only the
// rate and tax are saved it is worked back from them.
// Firebase cost: none here — works on documents the report already loaded.

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const WHT_SECTION_DEFS = [
  { key: 'payment', title: 'Withholdings — Payment', tax: 'WHT Income Tax Payment' },
  { key: 'receipt', title: 'Withholdings — Receipt', tax: 'WHT Income Tax Receipt' },
  { key: 'advance', title: 'Advance Tax — Section 236H', tax: 'Advance Tax 236H' },
];

const backTaxable = (wht, rate, fallback) => (Number(rate) > 0 ? r2((Number(wht) * 100) / Number(rate)) : r2(fallback));
const rateOf = (rate, wht, taxable) => {
  const r = Number(rate) || 0;
  if (r > 0) return r;
  return taxable ? Math.round((Number(wht) * 10000) / Number(taxable)) / 100 : 0;
};

/**
 * @param docs      posted documents in the period (and branch) to report on
 * @param opts.invoices  { id: invoice doc } — to show the invoice date / vendor bill no.
 * @param opts.vendors / opts.customers  { id: { ntn } }
 * @param opts.advanceLabel  company's advance-tax label (e.g. "Advance Tax 236H")
 * @returns { payment: Row[], receipt: Row[], advance: Row[] }
 */
export function buildWhtRows(docs, opts = {}) {
  const invoices = opts.invoices || {};
  const vendors = opts.vendors || {};
  const customers = opts.customers || {};
  const advanceTax = (opts.advanceLabel || '').trim() || 'Advance Tax 236H';
  const out = { payment: [], receipt: [], advance: [] };
  const ntnOf = (map, id) => (id && map[id] && (map[id].ntn || '')) || '';

  const push = (section, t, row) => out[section].push({
    date: t.docDate || '', transNo: t.docNumber || '', transId: t.id, transType: t.tranTypeCode,
    refNo: '', refDate: '', billNo: '', party: '', ntn: '', ...row,
  });

  // Cash / Bank Payment or Receipt: one row per invoice with WHT, plus advance WHT.
  const fromVoucher = (t, section, map, tax) => {
    const multi = t.receiptMode === 'multi-party' && Array.isArray(t.partyLines) && t.partyLines.length > 0;
    const groups = multi
      ? t.partyLines.map((l) => ({ partyId: l.partyId, party: l.partyName, gross: l.amount, allocs: l.invoiceAllocations, adv: l.advanceWhtAmount, rate: l.whtRate, total: l.whtAmount }))
      : [{ partyId: t.partyId, party: t.partyName, gross: t.totalAmount, allocs: t.invoiceAllocations, adv: t.advanceWhtAmount, rate: t.whtRate, total: t.whtAmount }];
    groups.forEach((g) => {
      const allocs = Array.isArray(g.allocs) ? g.allocs : [];
      const party = g.party || '';
      const ntn = ntnOf(map, g.partyId);
      let allocWht = 0;
      let allocAmt = 0;
      allocs.forEach((a) => {
        allocAmt += Number(a.allocatedAmount) || 0;
        const w = r2(a.whtAmount);
        if (w <= 0) return;
        allocWht += w;
        const inv = invoices[a.invoiceId] || {};
        const taxable = r2((Number(a.allocatedAmount) || 0) - (Number(a.adjustmentAmount) || 0));
        push(section, t, {
          refNo: a.docNumber || inv.docNumber || '', refDate: inv.docDate || '', billNo: inv.vendorInvoiceNo || '',
          party, ntn, tax, taxable, rate: rateOf(a.whtRate, w, taxable), wht: w,
        });
      });
      const adv = r2(g.adv);
      if (adv > 0) {
        const taxable = backTaxable(adv, g.rate, (Number(g.gross) || 0) - allocAmt);
        push(section, t, { refNo: 'Advance', party, ntn, tax, taxable, rate: rateOf(g.rate, adv, taxable), wht: adv });
      }
      // Older vouchers kept WHT only on the voucher (no split per invoice).
      const rest = r2((Number(g.total) || 0) - allocWht - adv);
      if (rest > 0.009) {
        const taxable = backTaxable(rest, g.rate, g.gross);
        push(section, t, {
          refNo: allocs.map((a) => a.docNumber).filter(Boolean).join(', '), party, ntn, tax, taxable, rate: rateOf(g.rate, rest, taxable), wht: rest,
        });
      }
    });
  };

  // WHT taken on the invoice itself (no payment voucher).
  const fromInvoice = (t, section, map, tax, sign = 1) => {
    const w = r2(t.whtAmount);
    if (w <= 0) return;
    const base = (Number(t.subtotal) || 0) - (Number(t.discountAmount) || 0);
    const taxable = backTaxable(w, t.whtRate, base);
    push(section, t, {
      // A return points to the invoice it reverses; an invoice is its own reference.
      refNo: sign < 0 ? (t.parentDocNumber || '') : '', billNo: t.vendorInvoiceNo || '',
      party: t.partyName || '', ntn: ntnOf(map, t.partyId), tax,
      taxable: r2(sign * taxable), rate: rateOf(t.whtRate, w, taxable), wht: r2(sign * w),
    });
  };

  const [pay, rec] = WHT_SECTION_DEFS;
  (docs || []).forEach((t) => {
    const tc = t.tranTypeCode;
    if (tc === 'CP' || tc === 'BP') fromVoucher(t, 'payment', vendors, pay.tax);
    else if (tc === 'CR' || tc === 'BR') fromVoucher(t, 'receipt', customers, rec.tax);
    else if (tc === 'PI' || tc === 'NPI' || tc === 'IPI') fromInvoice(t, 'payment', vendors, pay.tax);
    else if (tc === 'PR' || tc === 'NPR') fromInvoice(t, 'payment', vendors, pay.tax, -1);
    else if (tc === 'SI' && t.whtAddToTotal === true) fromInvoice(t, 'advance', customers, advanceTax);
    else if (tc === 'SI' || tc === 'NSI') fromInvoice(t, 'receipt', customers, rec.tax);
  });

  const order = (a, b) => (a.date || '').localeCompare(b.date || '') || (a.transNo || '').localeCompare(b.transNo || '');
  Object.values(out).forEach((rows) => rows.sort(order));
  return out;
}

/** Section totals: { taxable, wht, count } */
export const whtTotals = (rows) => ({
  count: rows.length,
  taxable: r2(rows.reduce((s, r) => s + (Number(r.taxable) || 0), 0)),
  wht: r2(rows.reduce((s, r) => s + (Number(r.wht) || 0), 0)),
});

/** Invoices referenced by payment / receipt rows that are not already loaded. */
export const missingInvoiceIds = (docs, loaded) => {
  const ids = new Set();
  (docs || []).forEach((t) => {
    if (!['CP', 'BP', 'CR', 'BR'].includes(t.tranTypeCode)) return;
    const lists = t.receiptMode === 'multi-party' && Array.isArray(t.partyLines)
      ? t.partyLines.map((l) => l.invoiceAllocations || [])
      : [t.invoiceAllocations || []];
    lists.flat().forEach((a) => { if (a && a.invoiceId && Number(a.whtAmount) > 0 && !loaded[a.invoiceId]) ids.add(a.invoiceId); });
  });
  return [...ids];
};
